import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, Mail, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useEventStream } from '../../hooks/useEventStream';
import { Button } from '../ui/button';

const DOMAIN_OPTIONS = [
  'staffing', 'quality', 'access', 'finance',
  'consumer', 'practice_management', 'population_health', 'compliance',
];

function tierColor(tier) {
  switch (tier) {
    case 'critical': return '#EF4444';
    case 'high': return '#F59E0B';
    case 'moderate': return '#2C5FA8';
    default: return '#10B981';
  }
}

function urgencyToTier(urgency) {
  switch (urgency) {
    case 'stat': return 'critical';
    case 'urgent': return 'high';
    default: return 'moderate';
  }
}

export default function InterventionQueue({ onResolve, onDismiss }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [reclassifyTarget, setReclassifyTarget] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const { getAccessToken } = useAuth();
  const { subscribe } = useEventStream();

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/signals/pending-review', {}, getAccessToken);
      const mapped = (data || []).map(s => {
        const cls = s.classification || {};
        const confidence = typeof cls.confidence === 'number' ? cls.confidence : null;
        const isLowConfidence = s.needs_review === true && s.staffing_action_status !== 'pending_review';
        return {
          id: s.signal_id,
          dbId: s.id,
          subject: s.subject || 'No Subject',
          sender: s.sender,
          preview: (s.body_deidentified || '').slice(0, 200),
          riskTier: urgencyToTier(cls.urgency),
          category: cls.sub_domain || 'absence',
          domain: cls.domain || 'staffing',
          confidence,
          isLowConfidence,
          receivedAt: s.received_at,
        };
      });
      setQueue(mapped);
    } catch {
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh on SSE notification
  useEffect(() => {
    const unsub = subscribe('notification.new', (data) => {
      if (data?.type === 'staffing_signal_pending_review' || data?.type === 'signal_low_confidence_review') load();
    });
    return unsub;
  }, [subscribe, load]);

  const handleResolve = async (signalId) => {
    if (!selectedUserId) return;
    try {
      await apiFetch(`/api/signals/${signalId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUserId }),
      }, getAccessToken);
      setResolveTarget(null);
      setSelectedUserId('');
      load();
      onResolve?.();
    } catch (err) {
      console.error('Resolve failed:', err);
    }
  };

  const handleReclassify = async (signal) => {
    if (!selectedDomain) return;
    try {
      await apiFetch(`/api/emails/${signal.dbId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reclassifiedDomain: selectedDomain }),
      }, getAccessToken);
      // Also dismiss the signal to clear needs_review
      await apiFetch(`/api/signals/${signal.id}/dismiss`, {
        method: 'POST',
      }, getAccessToken);
      setReclassifyTarget(null);
      setSelectedDomain('');
      load();
      onResolve?.();
    } catch (err) {
      console.error('Reclassify failed:', err);
    }
  };

  const handleDismiss = async (signalId) => {
    try {
      await apiFetch(`/api/signals/${signalId}/dismiss`, {
        method: 'POST',
      }, getAccessToken);
      load();
      onDismiss?.();
    } catch (err) {
      console.error('Dismiss failed:', err);
    }
  };

  const openResolveDialog = async (signal) => {
    setResolveTarget(signal);
    setSelectedUserId('');
    // Load staff list for user picker
    try {
      const staff = await apiFetch('/api/staffing/available-staff', {}, getAccessToken);
      setStaffList(staff || []);
    } catch {
      setStaffList([]);
    }
  };

  if (loading) {
    return (
      <div className="bg-nn-white rounded-[14px] shadow-card p-8 text-center">
        <p className="text-text-muted">Loading pending signals...</p>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ marginBottom: 16 }}><CheckCircle size={48} strokeWidth={2} color="#2EB87A" /></div>
          <h3 className="text-lg font-semibold" style={{ marginBottom: 8 }}>No interventions needed</h3>
          <p className="text-text-muted">All email signals have been auto-resolved or there are none pending.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">
            <Mail size={14} className="inline mr-2" />
            Pending email signals ({queue.length})
          </div>
          <div className="text-sm text-gray-500">Signals requiring manual review</div>
        </div>
        <div className="p-5 space-y-4">
          {queue.map(signal => (
            <div
              key={signal.id}
              style={{
                padding: 20,
                background: '#F9FAFB',
                borderRadius: 8,
                borderLeft: `4px solid ${signal.isLowConfidence ? '#F59E0B' : tierColor(signal.riskTier)}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
                <div className="min-w-0">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        background: signal.isLowConfidence ? '#FEF3C720' : `${tierColor(signal.riskTier)}20`,
                        color: signal.isLowConfidence ? '#92400E' : tierColor(signal.riskTier),
                      }}
                    >
                      {signal.isLowConfidence ? 'low confidence' : signal.riskTier}
                    </span>
                    {signal.confidence != null && (
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          background: signal.confidence < 0.7 ? '#FEF3C7' : '#D1FAE5',
                          color: signal.confidence < 0.7 ? '#92400E' : '#065F46',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {signal.confidence < 0.7 && <AlertTriangle size={10} />}
                        {Math.round(signal.confidence * 100)}%
                      </span>
                    )}
                    <span className="text-body text-text-muted truncate">{signal.domain} / {signal.category}</span>
                  </div>
                  <h4 className="text-base font-semibold truncate" style={{ marginBottom: 6 }}>{signal.subject}</h4>
                  <p className="text-body text-text-muted line-clamp-2" style={{ marginBottom: 8 }}>{signal.preview}</p>
                  {signal.sender && (
                    <div className="text-body-sm text-text-muted">
                      From: {signal.sender.name || signal.sender.email} ({signal.sender.email})
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                {signal.isLowConfidence ? (
                  <Button variant="primary" size="sm" type="button" onClick={() => { setReclassifyTarget(signal); setSelectedDomain(signal.domain); }}>
                    Reclassify domain
                  </Button>
                ) : (
                  <Button variant="primary" size="sm" type="button" onClick={() => openResolveDialog(signal)}>
                    Assign staff member
                  </Button>
                )}
                <Button variant="default" size="sm" type="button" onClick={() => handleDismiss(signal.id)}>
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Resolve dialog — user picker (staffing signals) */}
      {resolveTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setResolveTarget(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: 500,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              padding: 24,
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold" style={{ marginBottom: 16 }}>
              Assign staff member to this call-out
            </h3>
            <p className="text-body text-text-muted" style={{ marginBottom: 12 }}>
              <strong>{resolveTarget.subject}</strong>
            </p>
            {resolveTarget.sender && (
              <p className="text-body-sm text-text-muted" style={{ marginBottom: 16 }}>
                Email sender: {resolveTarget.sender.email} — select the staff member this person represents.
              </p>
            )}
            <select
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              <option value="">Select staff member...</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.name} — {s.role} {s.email ? `(${s.email})` : ''}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={!selectedUserId}
                onClick={() => handleResolve(resolveTarget.id)}
              >
                Confirm & create absence
              </Button>
              <Button variant="default" size="sm" type="button" onClick={() => setResolveTarget(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reclassify dialog — domain picker (low-confidence signals) */}
      {reclassifyTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setReclassifyTarget(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: 500,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              padding: 24,
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold" style={{ marginBottom: 16 }}>
              Reclassify email domain
            </h3>
            <p className="text-body text-text-muted" style={{ marginBottom: 12 }}>
              <strong>{reclassifyTarget.subject}</strong>
            </p>
            <p className="text-body-sm text-text-muted" style={{ marginBottom: 8 }}>
              Current classification: <strong>{reclassifyTarget.domain}</strong>
              {reclassifyTarget.confidence != null && (
                <span> ({Math.round(reclassifyTarget.confidence * 100)}% confidence)</span>
              )}
            </p>
            <p className="text-body-sm text-text-muted" style={{ marginBottom: 16 }}>
              Select the correct domain for this email:
            </p>
            <select
              value={selectedDomain}
              onChange={e => setSelectedDomain(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              {DOMAIN_OPTIONS.map(d => (
                <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={!selectedDomain}
                onClick={() => handleReclassify(reclassifyTarget)}
              >
                Confirm reclassification
              </Button>
              <Button variant="default" size="sm" type="button" onClick={() => setReclassifyTarget(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
